import { EncodingResult } from '@mode-vegas/vegas';
import * as angular from 'angular';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, first, map, startWith, switchMap, takeUntil } from 'rxjs/operators';

import {
  EmbedActions,
  EmbedActionType,
  EmbedBuilderActions,
  EmbedReportActions,
  EmbedReportState,
  ReportBuilderStore,
} from '@mode/reports/sandbox/contract';
import {
  ChartTypes,
  DataProfilingTypes,
  FileExportDomain,
  FlamingoTypes,
  SortSettingsTypes,
} from '@mode/shared/contract-common';
import {
  generateUUID,
  shareReplayDefault,
  debounceResize,
  getAppName,
  isBuilderMode,
  isPhantomMode,
  isReportMode,
} from '@mode/shared/util-js';
import { FeatureFlag, FeatureFlagsFacade } from '@mode/shared/contract-common';
import { AnalyticsEvent, AnalyticsProps, AnalyticsService } from '@mode/shared/util-segment';
import {
  ConditionalFormatting,
  ConditionalFormattingMap,
} from 'libs/shared/contract-common/src/lib/conditional-formatting.types';
import { DataProfiling, DataProfilingMap } from 'libs/shared/contract-common/src/lib/data-profiling.types';
import { MixableBaseController } from '../../../common/component';
import { VizTypes } from '../../../common/types';
import ReportLinkService from '../../../services/report-link-service';
import { EmbedChartSelectors, EmbedReportSelectors, FlamingoSelectors } from '../domain/embed-report.selectors';

class VegasTableController extends MixableBaseController {
  static $inject = [
    '$element',
    '$window',
    '$timeout',
    'ReportLinkService',
    'RxStoreService',
    'FeatureFlagsFacade',
    'AnalyticsService',
  ];

  onTitleChanged: (args: { $vizID: string; $title: string }) => void;
  onCaptionInfoChanged: (args: { $vizID: string; $captionInfo: ChartTypes.CaptionInfo }) => void;

  id: string;
  token: string;
  autoHeight: boolean;
  themeLoaded: boolean;
  renderInfo: Readonly<ChartTypes.VegasRenderInfo>;
  chartTitle: string;
  queryToken: string;
  isVirtualized: boolean;
  exploreEnabled = false;
  viewer: string;
  dataset: EmbedReportState.EmbedDataset;
  inViewport: boolean;
  canRender = true; // Whether the table meets the requirements for rendering
  disableDomCache = false;

  domCache: angular.IAugmentedJQuery;
  modeModel$: Observable<FlamingoTypes.ModeModel>;
  queryToken$: Observable<string>;
  isOrphan$: Observable<boolean>;
  canUndo$: Observable<boolean>;
  canRedo$: Observable<boolean>;
  private shownNotifier = new BehaviorSubject<boolean>(false);
  private shown$: Observable<boolean>;

  isMetricChart: boolean;
  disableTableCsvExport$: Observable<boolean>;

  showTitle = true;
  chartCaptionText: string;
  showCaptionText = true;
  captionLocation = ChartTypes.ChartCaptionPosition.Top;
  readonly copyText = `Copy to ${getAppName()} clipboard`;

  private content: FlamingoTypes.RecordsResult['content'];

  private columns: FlamingoTypes.RecordsResult['columns'];

  private paging$: Observable<FlamingoTypes.PagingResult>;
  private customConfig$: Observable<ChartTypes.FlatTableCustomConfig>;
  private isPurged$: Observable<boolean>;
  private isValid$: Observable<boolean>;
  private planFailed$: Observable<boolean>;
  private planError$: Observable<string>;
  private planErrorHelp$: Observable<string>;
  private tableExportIsInvalidated$ = new BehaviorSubject(false);
  noResults$: Observable<boolean>;
  private exportRowLimit$: Observable<number>;
  private dataProfiling$: Observable<DataProfiling | Readonly<DataProfilingMap>>;
  private conditionalFormatting$: Observable<ConditionalFormatting | Readonly<ConditionalFormattingMap>>;

  onRenderEnd: (args: { $visualizationRendered: boolean }) => void;
  onRenderStart: (args: { $vizId: string }) => void;
  onRecordsLoaded: () => void;

  constructor(
    private $elem: angular.IAugmentedJQuery,
    private $window: angular.IWindowService,
    private $timeout: angular.ITimeoutService,
    private reportLinkService: ReportLinkService,
    private rxStore: ReportBuilderStore,
    private featureFlagsFacade: FeatureFlagsFacade,
    private analyticsService: AnalyticsService
  ) {
    super();
    this.exportRowLimit$ = featureFlagsFacade
      .featureValueAsObservable<number>(FeatureFlag.CsvExportRowLimit)
      .pipe(map((limit) => limit / 1000));
    this.shown$ = this.shownNotifier.pipe(
      filter((shown) => shown),
      first() // Don't want to continue firing after being shown once
    );
  }

  $onInit() {
    this.token = this.id.split('_')[1];

    this.modeModel$ = this.rxStore.select(FlamingoSelectors.createModelSelector(this.token));

    this.queryToken$ = this.rxStore
      .select(EmbedChartSelectors.createQueryTokenSelector(this.token))
      .pipe(shareReplayDefault(), takeUntil(this.destroyed$));

    this.queryToken$
      .pipe(switchMap((queryToken) => this.rxStore.select(EmbedReportSelectors.createDatasetSelector(queryToken))))
      .subscribe((dataset) => {
        this.dataset = dataset;
      });

    this.queryToken$.subscribe((queryToken) => {
      this.queryToken = queryToken;
    });

    this.disableTableCsvExport$ = combineLatest([
      this.rxStore.select(EmbedChartSelectors.createDownloadsSelector(this.token)),
      this.tableExportIsInvalidated$,
    ]).pipe(
      map(([downloadStatus, invalidated]) => {
        return downloadStatus === FileExportDomain.ExportStatus.Init && !invalidated;
      })
    );

    const flamingoError$ = this.rxStore.select(FlamingoSelectors.createFriendlyErrorSelector(this.token));

    this.isPurged$ = this.queryToken$.pipe(
      switchMap((queryToken) => this.rxStore.select(EmbedChartSelectors.createDatasetResultSelector(queryToken))),
      map((datasetResult) => datasetResult.isPurged)
    );

    this.isValid$ = this.shown$.pipe(
      switchMap(() =>
        this.rxStore.select(FlamingoSelectors.createIsValidSelector(this.token)).pipe(shareReplayDefault())
      )
    );
    this.planFailed$ = this.rxStore.select(FlamingoSelectors.createPlanFailedSelector(this.token));
    this.planError$ = flamingoError$.pipe(map((err) => err.msg));
    this.planErrorHelp$ = flamingoError$.pipe(map((err) => err.helpText));
    const planLoading$ = this.shown$.pipe(
      switchMap(() => this.rxStore.select(FlamingoSelectors.createPlanLoadingSelector(this.token))),
      startWith(true), // Start with true since shown$ can take time to fire
      shareReplayDefault()
    );

    this.isOrphan$ = this.rxStore.select(EmbedChartSelectors.createIsOrphanChartSelector(this.token));

    const historyState$ = this.rxStore.select(EmbedChartSelectors.createEmbedStateSelector(this.token));
    this.canUndo$ = historyState$.pipe(map((history) => history.canUndo));
    this.canRedo$ = historyState$.pipe(map((history) => history.canRedo));

    const records$ = this.rxStore.select(FlamingoSelectors.createRecordsSelector(this.token)).pipe(
      filter((p) => p != null),
      takeUntil(this.destroyed$)
    );

    const columns$ = this.rxStore.select(FlamingoSelectors.createColumnsSelector(this.token)).pipe(
      filter((p) => p != null),
      takeUntil(this.destroyed$)
    );

    this.paging$ = this.rxStore.select(FlamingoSelectors.createPagingSelector(this.token)).pipe(
      filter((p) => p != null),
      shareReplayDefault(),
      takeUntil(this.destroyed$)
    );

    this.noResults$ = this.paging$.pipe(map((paging) => paging == null || paging.total <= 0));

    this.customConfig$ = this.rxStore.select(EmbedReportSelectors.createAlamodeConfigSelector(this.token));

    columns$.subscribe((columns) => {
      this.columns = columns;
    });

    records$.subscribe((records) => {
      this.content = records;
      this.canRender = true;

      // Trigger event to `mode-chart` to hide placeholder when new records are fetched
      this.onRecordsLoaded();
    });

    combineLatest([this.isValid$, this.planFailed$, planLoading$])
      .pipe(
        takeUntil(this.destroyed$),
        filter(([isValid, failed, loading]) => (!isValid && !loading) || failed)
      )
      .subscribe(() => {
        this.renderEnd(false);
        this.canRender = false;
      });

    this.dataProfiling$ = this.rxStore.select(FlamingoSelectors.createDataProfilingSelector(this.token)).pipe(
      filter((p) => p != null),
      takeUntil(this.destroyed$)
    );

    this.conditionalFormatting$ = this.rxStore
      .select(FlamingoSelectors.createConditionalFormattingSelector(this.token))
      .pipe(
        filter((p) => p != null),
        takeUntil(this.destroyed$)
      );
  }

  $onChanges(changes: angular.IOnChangesObject) {
    if (changes.renderInfo && this.renderInfo != null) {
      this.chartTitle = this.renderInfo.title;
      this.chartCaptionText = this.renderInfo.caption.text;
      this.showCaptionText = !this.renderInfo.caption.isHidden;
      this.showTitle = !this.renderInfo.caption.isTitleHidden;
      this.captionLocation =
        this.renderInfo.caption.position === ChartTypes.ChartCaptionPosition.Bottom
          ? ChartTypes.ChartCaptionPosition.Bottom
          : ChartTypes.ChartCaptionPosition.Top;

      if (
        changes.renderInfo.previousValue?.encoding != null &&
        changes.renderInfo.previousValue.encoding !== changes.renderInfo.currentValue.encoding
      ) {
        this.tableExportIsInvalidated$.next(true);
      }
    }

    if (changes.inViewport && this.inViewport) {
      this.shownNotifier.next(true);
    }

    if (changes.inViewport && !this.disableDomCache && this.domCache != null) {
      if (this.inViewport) {
        this.domCache[0].removeAttribute('style');
        this.onRenderEnd({ $visualizationRendered: true });
      } else {
        this.domCache[0].setAttribute('style', 'display: none');
      }
    } else if (changes.inViewport && !this.canRender) {
      // Remove placeholder for errored charts when back in viewport
      if (this.inViewport) {
        this.renderEnd(false);
      }
    }
  }

  setAndCacheTableHeight() {
    if (this.isVirtualized) {
      this.domCache = this.$elem.find('.table-renderer');
      this.$elem.find('.chart').css('height', this.domCache.height());
    }
  }

  renderStart() {
    this.onRenderStart({ $vizId: this.id });
  }

  renderEnd(visualizationRendered = true) {
    this.setAndCacheTableHeight();
    this.onRenderEnd({ $visualizationRendered: visualizationRendered });
  }

  onAction(action: EmbedActions) {
    this.rxStore.dispatch(action);
  }

  setOffset(offset: number) {
    this.rxStore.dispatch(new EmbedReportActions.SetTableOffset(this.token, offset));
  }

  /**
   * Update the tableColumnSearch and triggers re-query
   */
  setTableColumnSearch(args: { token: string; columnSearchModel: ChartTypes.TableColumnSearch }) {
    this.rxStore.dispatch(new EmbedReportActions.SetTableColumnSearch(args.token, args.columnSearchModel));
  }

  updateEncoding(encResult: EncodingResult) {
    this.rxStore.dispatch(new EmbedReportActions.UpdateEncoding(this.token, encResult));
  }

  removeViz() {
    this.rxStore.dispatch(new EmbedBuilderActions.RemoveViz(this.token, VizTypes.Table));
  }

  isBuilderMode() {
    return isBuilderMode(this.$window.document.body);
  }

  isReportMode() {
    return isReportMode(this.$window.document.body);
  }

  isPhantomMode() {
    return isPhantomMode(this.$window);
  }

  titleEditDisabled() {
    return this.isReportMode();
  }

  updateTitle($title: string) {
    this.chartTitle = $title;
    this.onTitleChanged({ $vizID: this.id, $title });
  }

  updateCaptionText($captionText: string) {
    this.chartCaptionText = $captionText;
    this.onCaptionInfoChanged({
      $vizID: this.id,
      $captionInfo: { ...this.renderInfo.caption, text: $captionText },
    });
  }

  copySuccess() {
    this.onAction({
      type: EmbedActionType.ShowAlert,
      success: true,
      message: 'Link to chart copied successfully',
    });
  }

  copyChart() {
    this.analyticsService.track(AnalyticsEvent.Name.UiClicked, {
      location: AnalyticsEvent.SubContext.ReportView,
      action: 'Copy Chart',
      resource: AnalyticsEvent.Context.Report,
      id: this.token,
    });
    this.onAction({
      type: EmbedActionType.CopyChart,
      token: this.token,
    });
  }

  isLinkable() {
    return this.reportLinkService.isLinkable();
  }

  linkToData($event) {
    if (this.isLinkable()) {
      $event.preventDefault();
      $event.stopPropagation();
      this.reportLinkService.linkToPage(this.queryToken, 'data');
    }
  }

  flamingoCsv() {
    this.tableExportIsInvalidated$.next(false);
    this.rxStore.dispatch(new EmbedReportActions.ExportChartCsv(this.token));
  }

  linkToQuery($event) {
    if (this.isLinkable()) {
      $event.preventDefault();
      $event.stopPropagation();
      this.reportLinkService.linkToPage(this.queryToken, 'query');
    }
  }

  linkToLightbox($event) {
    this.noResults$.pipe(first()).subscribe((noResults) => {
      if (!noResults && this.isLinkable()) {
        const analyticsProps = this.getAnalyticsProps();

        $event.preventDefault();
        $event.stopPropagation();

        this.onAction({
          type: EmbedActionType.ExploreChart,
          chartToken: this.token,
          props: analyticsProps,
        });
      }
    });
  }

  getAnalyticsProps(): AnalyticsProps {
    return {
      event_id: generateUUID(),
      product_context: AnalyticsEvent.Context.Report,
      sub_context: AnalyticsEvent.SubContext.Chart,
      chart_id: this.id,
      chart_type: 'table',
    };
  }

  undo(ev: Event) {
    ev.preventDefault();
    this.onAction({
      type: EmbedActionType.Back,
      chartToken: this.id.replace('chart_', ''),
    });
  }

  redo(ev: Event) {
    ev.preventDefault();
    this.onAction({
      type: EmbedActionType.Forward,
      chartToken: this.id.replace('chart_', ''),
    });
  }

  stopEventPropagation($event) {
    $event.stopPropagation();
  }

  onHeaderSizeChange() {
    debounceResize(this.$window);
  }

  handleGetDataProfiling($event: DataProfilingTypes.DataProfilingChartDetails) {
    if (this.isReportMode()) {
      this.rxStore.dispatch(new EmbedReportActions.GetDataProfiling($event.token, $event.cols, $event.referenceId));
    } else {
      this.rxStore.dispatch(new EmbedBuilderActions.GetDataProfiling($event.token, $event.cols, $event.referenceId));
    }
  }

  onSortSettingsChange($event: SortSettingsTypes.SortSettingsEvent) {
    if (this.isReportMode()) {
      this.rxStore.dispatch(
        new EmbedReportActions.UpdateSortSettings(this.token, {
          ...$event,
          chartTitle: this.chartTitle || 'Untitled chart',
        })
      );
    } else {
      this.rxStore.dispatch(new EmbedBuilderActions.UpdateSortSettings(this.token, $event));
    }
  }
}

export const VegasTableComponent: angular.IComponentOptions = {
  bindings: {
    id: '@',
    inViewport: '<',
    renderInfo: '<',
    isVirtualized: '<',
    viewer: '<',
    autoHeight: '<',
    exploreEnabled: '<',
    disableDomCache: '<',
    onRenderStart: '&',
    onRenderEnd: '&',
    onRecordsLoaded: '&',
    themeLoaded: '<',
    onTitleChanged: '&',
    onCaptionInfoChanged: '&',
    isMetricChart: '<',
  },
  controller: VegasTableController,
  controllerAs: 'ctrl',
  template: `
  <div class="editor-module editor-chart editor-multi mode-object">
    <!-- Intermediary Messages Begin -->
    <!-- chart-error must come before chart-content to handle errors in charts with height: auto -->
    <div class="chart-error" ng-if="ctrl.planFailed$ | async:this">
      <h3 ng-bind="ctrl.planError$ | async:this"></h3>
      <p ng-bind="ctrl.planErrorHelp$ | async:this"></p>
    </div>
    <div ng-if="(ctrl.isPurged$ | async:this)" class="chart-error">
      <h3>No results</h3>
    </div>
    <div ng-if="(ctrl.isValid$ | async:this) === false && !(ctrl.isPurged$ | async:this)" class="chart-error">
      <h3>Sorry, the table configuration or data is invalid.</h3>
      <p class="sub-message">Try checking the table configuration for invalid fields</p>
    </div>
    <!-- @Intermediary Messages End -->
    <div class="chart-content">
      <!-- Component Header Begins -->
      <div class="chart-header caption-chart-header" ng-if="ctrl.isReportMode() || ctrl.isBuilderMode()">
        <mode-in-place-input
          ng-if="ctrl.showTitle"
          placeholder="Untitled chart"
          [readonly]="ctrl.isReportMode()"
          [text]="ctrl.chartTitle"
          [maxchars]="250"
          (text-change)="ctrl.updateTitle($event)"
          ng-dblclick="ctrl.stopEventPropagation($event)"
        ></mode-in-place-input>
      <div class="new-viz-actions" ng-if="(ctrl.isValid$ | async:this)">
          <button class="new-viz-action action-left hide-2 plus-plan-only"
              ng-click="ctrl.undo($event)"
              ng-class="{ disabled: !(ctrl.canUndo$ | async:this)}">
            <svg-icon type="'back'" uib-tooltip="Back"></svg-icon>
          </button>

          <button class="new-viz-action action-right hide-2 plus-plan-only"
              ng-click="ctrl.redo($event)"
              ng-class="{ disabled: !(ctrl.canRedo$ | async:this)}">
            <svg-icon type="'forward'" uib-tooltip="Redo" ng-if="ctrl.viewer !== 'whiteLabel'"></svg-icon>
            <!--
              Workaround to place tooltip on the left on WLE so it doesn't conflict with scrollbar when it's the last element in a row.
              Ideally, this would be better handled with a WLE CSS class that would only turn this on when it's the last
              item in a row.
            -->
            <svg-icon type="'forward'" uib-tooltip="Redo" ng-if="ctrl.viewer === 'whiteLabel'" tooltip-placement="left"></svg-icon>
          </button>

          <div class="new-viz-actions__section" ng-if="ctrl.viewer !== 'whiteLabel'">
            <copy-link-to-viz link="ctrl.renderInfo._vizReportRunLink" ng-if="!ctrl.isMetricChart">
              <button
                class="new-viz-action hide-2"
                uib-tooltip="Get Share Link"
                copy-to-clipboard
                clipboard-text="ctrl.renderInfo._vizReportRunLink"
                clipboard-success="ctrl.copySuccess()"
                clipboard-enabled="isClipboardEnabled">
                <svg-icon type="'copy-link'"></svg-icon>
              </button>
            </copy-link-to-viz>
            <button class="new-viz-action">
              <kebab-menu targetable="true">
                <li menu-item="exp" ng-if="!ctrl.viewer" ng-click="ctrl.copyChart()">{{ ctrl.copyText }}</li>
                <li menu-item="exp" ng-click="ctrl.linkToQuery($event)" ng-href="{{ ctrl.dataset.reportQueryUrl }}" ng-if="!ctrl.viewer">View source <span class="mode-show-embed-inline">in Mode</span></li>
                <li menu-item="exp" ng-click="ctrl.linkToData($event)" ng-href="{{ ctrl.dataset.reportDataUrl }}">View raw data <span class="mode-show-embed-inline">in Mode</span></li>
                <li
                  menu-item="exp"
                  ng-click="ctrl.flamingoCsv()"
                  ng-if="ctrl.viewer !== 'whiteLabel'"
                  ng-class="{ disabled: (ctrl.disableTableCsvExport$ | async:this)}"
                  class="table-export-link">
                  Export chart data to CSV<br />
                  <span class="menu-item-description">
                    Tables with over {{ ctrl.exportRowLimit$ | async:this }}K rows will be truncated
                  </span>
                </li>
              </kebab-menu>
            </button>
          </div>

          <div class="new-viz-actions__section" ng-if="!ctrl.viewer">
            <span class="divider"></span>

            <a
              class="viz-explore-action btn btn-default"
              ng-if="ctrl.exploreEnabled"
              ng-class="{ disabled: (ctrl.noResults$ | async:this) }"
              ng-href="{{ ctrl.renderInfo._vizReportRunLink }}"
              ng-click="ctrl.linkToLightbox($event)">
              Explore
            </a>

            <a class="new-viz-action"
              ng-if="!ctrl.exploreEnabled && !ctrl.isMetricChart"
              ng-href="{{ ctrl.renderInfo._vizReportRunLink }}"
              ng-click="ctrl.linkToLightbox($event)" class="new-viz-action">
              <svg-icon class="mode-hide-embed" type="'toggle-expanded-view'" uib-tooltip="Expand"></svg-icon>
              <svg-icon class="mode-show-embed-flex-inline" type="'toggle-expanded-view'" uib-tooltip="Expand in Mode" tooltip-placement="left"></svg-icon>
            </a>

            <span class="new-viz-action no-border no-action"
                ng-if="ctrl.isMetricChart">
                <svg-icon class="mode-hide-embed" type="'metric-badge'" tooltip-class="tooltip-pre-wrap" tooltip-placement="left" uib-tooltip="This chart is powered by dbt Metrics"></svg-icon>
                <svg-icon class="mode-show-embed-flex-inline" type="'metric-badge'" uib-tooltip="This chart is powered by dbt Metrics" tooltip-placement="left"></svg-icon>
              </span>
          </div>
        </div>
      </div>
      <!-- Component Header Ends -->

      <!-- Table Container Begins -->
      <div class="chart" >
        <div class="table-renderer" ng-class="{ 'with-no-title': !ctrl.showTitle}">
          <div class="caption-container"
            ng-if="ctrl.showCaptionText"
            ng-class="{ 'caption-position-bottom': (ctrl.captionLocation === 'bottom' )}">
            <mode-in-place-textarea
              class="chart-caption"
              placeholder="Click to add chart description. Shift-enter for new line."
              [maxchars]="360"
              [text]="ctrl.chartCaptionText"
              [readonly]="ctrl.isReportMode()"
              (text-change)="ctrl.updateCaptionText($event)"
              (size-change)="ctrl.onHeaderSizeChange()"
              ng-mousedown="ctrl.stopEventPropagation($event)"
              ng-dblclick="ctrl.stopEventPropagation($event)">
            </mode-in-place-textarea>
          </div>
          <mode-table-renderer
            ng-if="ctrl.themeLoaded &&
                  ctrl.renderInfo.chartType === 'table' &&
                  (ctrl.isValid$ | async:this) &&
                  (ctrl.inViewport || ctrl.domCache != null)"
            [model]="ctrl.modeModel$ | async:this"
            [query-token]="ctrl.queryToken$ | async:this"
            [token]="ctrl.token"
            [columns]="ctrl.columns"
            [content]="ctrl.content"
            [paging]="ctrl.paging$ | async:this"
            [encoding]="ctrl.renderInfo.encoding"
            [field-formats]="ctrl.renderInfo.fieldFormats"
            [format]="ctrl.renderInfo.format"
            [show-column-menu]="ctrl.isReportMode()"
            [custom-config]="ctrl.customConfig$ | async:this"
            [auto-height]="ctrl.autoHeight"
            [data-profiling]="ctrl.dataProfiling$ | async:this"
            [conditional-formatting]="ctrl.conditionalFormatting$ | async: this"
            (set-offset)="ctrl.setOffset($event)"
            (set-table-column-search)="ctrl.setTableColumnSearch($event)"
            (update-encoding)="ctrl.updateEncoding($event)"
            (set-table-height)="ctrl.setAndCacheTableHeight()"
            (render-start)="ctrl.renderStart()"
            (render-end)="ctrl.renderEnd()"
            (get-data-profiling)="ctrl.handleGetDataProfiling($event)"
            (handle-sort-settings)="ctrl.onSortSettingsChange($event)"
          ></mode-table-renderer>
        </div>
      </div>
      <!-- @Table Container Ends -->
    </div>
  </div>
  `,
};
